import React, { useRef, useState } from "react";
import { Box, Typography, Button, Paper } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import { ContentCopy, Download, CheckCircle } from "@mui/icons-material";

interface HealthKitQRGeneratorProps {
  studyCode: string;
  studyName: string;
}


const sanitizeFileSegment = (value: string): string => {
  const sanitized = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return sanitized || "study";
};

const HealthKitQRGenerator: React.FC<HealthKitQRGeneratorProps> = ({
  studyCode,
  studyName,
}) => {
  const [copied, setCopied] = useState(false);
  const qrContainerRef = useRef<HTMLDivElement | null>(null);

  const clientBaseUrl = process.env.REACT_APP_CLIENT_URL || window.location.origin;
  const enrollmentUrl = `${clientBaseUrl}/app/enroll?studyCode=${encodeURIComponent(
    studyCode
  )}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(enrollmentUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const downloadUrl = (url: string, filename: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
  };

  const handleDownloadQR = () => {
    const svgElement = qrContainerRef.current?.querySelector("svg");
    if (!svgElement) {
      return;
    }

    const svgMarkup = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgMarkup], {
      type: "image/svg+xml;charset=utf-8",
    });
    const svgUrl = URL.createObjectURL(svgBlob);

    const image = new Image();
    image.onload = () => {
      const size = Number(svgElement.getAttribute("width") || "220");
      const scale = 3;
      const canvas = document.createElement("canvas");
      canvas.width = size * scale;
      canvas.height = size * scale;

      const context = canvas.getContext("2d");
      if (!context) {
        URL.revokeObjectURL(svgUrl);
        const fallbackSvgUrl = URL.createObjectURL(svgBlob);
        downloadUrl(
          fallbackSvgUrl,
          `${sanitizeFileSegment(studyCode)}-healthkit-enrollment-qr.svg`
        );
        setTimeout(() => URL.revokeObjectURL(fallbackSvgUrl), 0);
        return;
      }

      context.scale(scale, scale);
      context.drawImage(image, 0, 0, size, size);
      const pngUrl = canvas.toDataURL("image/png");
      downloadUrl(
        pngUrl,
        `${sanitizeFileSegment(studyCode)}-healthkit-enrollment-qr.png`
      );
      URL.revokeObjectURL(svgUrl);
    };

    image.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      const fallbackSvgUrl = URL.createObjectURL(svgBlob);
      downloadUrl(
        fallbackSvgUrl,
        `${sanitizeFileSegment(studyCode)}-healthkit-enrollment-qr.svg`
      );
      setTimeout(() => URL.revokeObjectURL(fallbackSvgUrl), 0);
    };

    image.src = svgUrl;
  };

  return (
    <Paper sx={{ p: 3, textAlign: "center" }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        HealthKit Enrollment QR Code
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Reusable enrollment QR code for {studyName}. Participants using Apple
        Health should scan this with the StudySync iOS app.
      </Typography>

      <Box
        ref={qrContainerRef}
        sx={{
          p: 3,
          backgroundColor: "common.white",
          borderRadius: 2,
          display: "inline-block",
          mb: 3,
        }}
      >
        <QRCodeSVG
          value={enrollmentUrl}
          size={220}
          level="M"
          includeMargin={false}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={copied ? <CheckCircle /> : <ContentCopy />}
          onClick={handleCopyLink}
          color={copied ? "success" : "primary"}
        >
          {copied ? "Copied!" : "Copy Link"}
        </Button>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleDownloadQR}
        >
          Download QR Code
        </Button>
      </Box>
    </Paper>
  );
};

export default HealthKitQRGenerator;
